import { initializeApp, getApps } from 'firebase/app';
import { getFirestore, collection, doc, onSnapshot, setDoc, deleteDoc, updateDoc, deleteField } from 'firebase/firestore';

const firebaseConfig = {
  apiKey: "AIzaSyDhOyoPUHocR_vYxZ-B8EGc2Vhg89EzOjY",
  authDomain: "day-ones-d198a.firebaseapp.com",
  projectId: "day-ones-d198a",
  storageBucket: "day-ones-d198a.firebasestorage.app",
  messagingSenderId: "57201093193",
  appId: "1:57201093193:web:9c5228e1d43073a8612d98"
};

let db = null;
let isFirebaseActive = false;

// Attempt Firebase initialization
try {
  if (firebaseConfig.apiKey && firebaseConfig.apiKey !== "") {
    const app = getApps().length === 0 ? initializeApp(firebaseConfig) : getApps()[0];
    db = getFirestore(app);
    isFirebaseActive = true;
    console.log("🔥 Connected to Firebase Firestore successfully!");
  } else {
    // Attempt auto-config if running in a deployed environment (where init.json is served automatically)
    // We will do this asynchronously in our subscribers, but print a warning for local dev here.
    console.log("ℹ️ No Firebase API Key found in firebase.js. Operating in rich Local Sync mode.");
  }
} catch (e) {
  console.error("⚠️ Failed to initialize Firebase:", e);
}

// Setup a Broadcast Channel for real-time synchronization between browser tabs/visitors locally
const syncChannel = typeof window !== 'undefined' && window.BroadcastChannel
  ? new BroadcastChannel('dayones_sync')
  : null;

/**
 * Subscribes to a data collection in real-time.
 * If Firebase is active, listens to Firestore.
 * If Firebase is inactive, listens to LocalStorage and cross-tab BroadcastChannel events.
 * 
 * @param {string} collectionName Name of the collection/key
 * @param {function} callback Callback function receiving the updated array/object
 */
export function subscribeToCollection(collectionName, callback) {
  let unsubscribeFn = null;
  let isSubscribedToFirestore = false;

  const doSubscribe = () => {
    if (isFirebaseActive && db) {
      try {
        const colRef = collection(db, collectionName);
        unsubscribeFn = onSnapshot(colRef, (snapshot) => {
          const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          callback(data);
        }, (err) => {
          console.error(`Firestore subscribe error for ${collectionName}:`, err);
          if (!isSubscribedToFirestore) {
            unsubscribeFn = fallbackSubscribe(collectionName, callback);
          }
        });
        isSubscribedToFirestore = true;
      } catch (e) {
        console.error(`Error hooking Firestore for ${collectionName}:`, e);
        unsubscribeFn = fallbackSubscribe(collectionName, callback);
      }
    } else {
      unsubscribeFn = fallbackSubscribe(collectionName, callback);
    }
  };

  doSubscribe();

  // Listen for dynamic Firebase activation
  const handleFirebaseActivated = () => {
    if (!isSubscribedToFirestore && isFirebaseActive && db) {
      console.log(`🔄 Dynamic Upgraded subscriber for '${collectionName}' to live Firestore!`);
      if (unsubscribeFn) {
        try {
          unsubscribeFn();
        } catch (err) { }
      }
      doSubscribe();
    }
  };

  if (typeof window !== 'undefined') {
    window.addEventListener('firebase-activated', handleFirebaseActivated);
  }

  return () => {
    if (unsubscribeFn) {
      try {
        unsubscribeFn();
      } catch (err) { }
    }
    if (typeof window !== 'undefined') {
      window.removeEventListener('firebase-activated', handleFirebaseActivated);
    }
  };
}

function fallbackSubscribe(collectionName, callback) {
  // Read initial from LocalStorage
  const readLocal = () => {
    try {
      const stored = localStorage.getItem(`dayones_${collectionName}`);
      if (stored) {
        return JSON.parse(stored);
      }
      return [];
    } catch (e) {
      return [];
    }
  };

  // Deliver initial state
  callback(readLocal());

  // Listen to local window storage events (cross-origin/same-domain tabs)
  const handleStorageChange = (e) => {
    if (e.key === `dayones_${collectionName}`) {
      callback(readLocal());
    }
  };

  // Listen to active tab broadcast channels (instantaneous local sync)
  const handleBroadcast = (e) => {
    if (e.data && e.data.collection === collectionName) {
      callback(readLocal());
    }
  };

  window.addEventListener('storage', handleStorageChange);
  if (syncChannel) {
    syncChannel.addEventListener('message', handleBroadcast);
  }

  // Return unsubscribe function
  return () => {
    window.removeEventListener('storage', handleStorageChange);
    if (syncChannel) {
      syncChannel.removeEventListener('message', handleBroadcast);
    }
  };
}

/**
 * Saves or updates a document in a collection.
 */
export async function saveDocument(collectionName, docId, data) {
  const cleanData = { ...data };
  delete cleanData.id; // Avoid redundant ID inside document fields

  if (isFirebaseActive && db) {
    try {
      const docRef = doc(db, collectionName, String(docId));
      await setDoc(docRef, cleanData, { merge: true });
      return;
    } catch (e) {
      console.error(`Firestore save document failed:`, e);
    }
  }

  // Local Storage Fallback
  try {
    const key = `dayones_${collectionName}`;
    const current = JSON.parse(localStorage.getItem(key) || '[]');
    const index = current.findIndex(item => String(item.id) === String(docId));

    const updatedDoc = { id: String(docId), ...cleanData };
    if (index >= 0) {
      current[index] = updatedDoc;
    } else {
      current.push(updatedDoc);
    }

    localStorage.setItem(key, JSON.stringify(current));
    triggerLocalSync(collectionName);
  } catch (e) {
    console.error("Local save failed:", e);
  }
}

/**
 * Deletes a document from a collection.
 */
export async function deleteDocument(collectionName, docId) {
  if (isFirebaseActive && db) {
    try {
      const docRef = doc(db, collectionName, String(docId));
      await deleteDoc(docRef);
      return;
    } catch (e) {
      console.error(`Firestore delete failed:`, e);
    }
  }

  // Local Storage Fallback
  try {
    const key = `dayones_${collectionName}`;
    const current = JSON.parse(localStorage.getItem(key) || '[]');
    const filtered = current.filter(item => String(item.id) !== String(docId));

    localStorage.setItem(key, JSON.stringify(filtered));
    triggerLocalSync(collectionName);
  } catch (e) {
    console.error("Local delete failed:", e);
  }
}

/**
 * Removes a specific field from a document completely.
 */
export async function removeFieldFromDocument(collectionName, docId, fieldName) {
  if (isFirebaseActive && db) {
    try {
      const docRef = doc(db, collectionName, String(docId));
      await updateDoc(docRef, {
        [fieldName]: deleteField()
      });
      return;
    } catch (e) {
      console.error(`Firestore remove field failed:`, e);
    }
  }

  // Local Storage Fallback — fieldName may be a dot-path (e.g. "votes.alice")
  // for a nested key, matching the Firestore dot-path field semantics above.
  try {
    const key = `dayones_${collectionName}`;
    const current = JSON.parse(localStorage.getItem(key) || '[]');
    const index = current.findIndex(item => String(item.id) === String(docId));
    if (index >= 0) {
      const path = fieldName.split('.');
      let target = current[index];
      for (let i = 0; i < path.length - 1 && target != null; i++) {
        target = target[path[i]];
      }
      if (target != null) delete target[path[path.length - 1]];
      localStorage.setItem(key, JSON.stringify(current));
      triggerLocalSync(collectionName);
    }
  } catch (e) {
    console.error("Local remove field failed:", e);
  }
}

// Broadcasts an instant event to other tabs to reload storage
function triggerLocalSync(collectionName) {
  if (syncChannel) {
    syncChannel.postMessage({ collection: collectionName, timestamp: Date.now() });
  }
  // Dispatch a dummy storage event so the current tab's listeners might fire if appropriate
  window.dispatchEvent(new Event('storage'));
}

/**
 * Dynamic setup initializer for when deployed.
 * Attempts to fetch configuration directly from hosting config endpoint.
 */
export async function autoDetectFirebase() {
  try {
    const response = await fetch('/__/firebase/init.json');
    if (response.ok) {
      const activeConfig = await response.json();
      const app = getApps().length === 0 ? initializeApp(activeConfig) : getApps()[0];
      db = getFirestore(app);
      isFirebaseActive = true;
      console.log("🔥 Dynamically configured Firebase Firestore using local environment metadata!");
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new Event('firebase-activated'));
      }
      return true;
    }
  } catch (e) {
    // Standard fail for local development or non-hosting server
  }
  return false;
}
